/**
 * Article Gallery Lightbox
 * Integrates MagicZoom Plus for article image galleries.
 * Follows the same pattern as magiczoomplus-integration.js for PDP.
 */
(() => {
  'use strict';

  const pendingSwitch = new Map();
  let hooksInstalled = false;

  /**
   * Checks if MagicZoom is loaded
   * @returns {boolean}
   */
  const isMagicZoomReady = () => typeof MagicZoom !== 'undefined';

  /**
   * Extracts zoom ID from MagicZoom callback argument
   * @param {string|HTMLElement|Object} arg - Callback argument
   * @returns {string|null}
   */
  const getZoomIdFromArg = (arg) => {
    if (typeof arg === 'string') return arg;
    return arg?.id || (arg?.getAttribute ? arg.getAttribute('id') : null);
  };

  /**
   * Hooks MagicZoom callbacks for gallery sync
   */
  const ensureExpandHook = () => {
    if (hooksInstalled) return;

    const hookOptions = (options) => {
      if (!options || options._articleGalleryHooked) return;

      const previousOpen = options.onExpandOpen;

      options.onExpandOpen = function onExpandOpen(arg) {
        if (typeof previousOpen === 'function') {
          try {
            previousOpen.apply(this, arguments);
          } catch (e) { /* ignore */ }
        }

        const zoomId = getZoomIdFromArg(arg);
        if (!zoomId) return;

        const index = pendingSwitch.get(zoomId);
        if (index === undefined) return;

        pendingSwitch.delete(zoomId);

        try {
          MagicZoom.switchTo(zoomId, index);
        } catch (e) { /* ignore */ }
      };

      options._articleGalleryHooked = true;
    };

    if (!window.mzOptions) {
      window.mzOptions = {};
    }
    if (!window.mzMobileOptions) {
      window.mzMobileOptions = {};
    }

    hookOptions(window.mzOptions);
    hookOptions(window.mzMobileOptions);
    hooksInstalled = true;
  };

  /**
   * Article Gallery Lightbox Web Component
   */
  class ArticleGalleryLightbox extends HTMLElement {
    constructor() {
      super();
      this.mzId = this.dataset.mzId;
    }

    connectedCallback() {
      this.triggers = this.querySelectorAll('.article-callout__gallery-trigger');
      this.mzAnchor = this.querySelector('.MagicZoomPlus');

      if (!this.triggers.length || !this.mzAnchor) return;

      this.waitForMagicZoom();
    }

    /**
     * Waits for MagicZoom then initializes
     */
    waitForMagicZoom() {
      const maxAttempts = 50;
      let attempts = 0;

      const check = () => {
        attempts++;

        if (isMagicZoomReady()) {
          ensureExpandHook();

          /* Wait for MagicZoom to process elements */
          setTimeout(() => {
            this.initTriggers();
          }, 100);
          return;
        }

        if (attempts < maxAttempts) {
          setTimeout(check, 100);
        }
      };

      check();
    }

    /**
     * Initializes click handlers
     */
    initTriggers() {
      this.triggers.forEach((trigger) => {
        trigger.addEventListener('click', (event) => {
          event.preventDefault();

          if (!isMagicZoomReady()) return;

          const imageIndex = parseInt(trigger.dataset.imageIndex, 10);
          if (isNaN(imageIndex)) return;

          this.openLightbox(imageIndex);
        });
      });
    }

    /**
     * Opens lightbox at specified image index
     * @param {number} imageIndex - Zero-based index
     */
    openLightbox(imageIndex) {
      if (!this.mzId) return;

      /* Queue switch for non-first images */
      if (imageIndex > 0) {
        pendingSwitch.set(this.mzId, imageIndex);
      }

      try {
        MagicZoom.expand(this.mzId);
      } catch (e) { /* ignore */ }
    }
  }

  if (!customElements.get('article-gallery-lightbox')) {
    customElements.define('article-gallery-lightbox', ArticleGalleryLightbox);
  }
})();
